import { createFeatureSelector, createSelector } from '@ngrx/store';
import { SubscriptionState } from './subscription.state';

// Feature selector
export const selectSubscriptionState = createFeatureSelector<SubscriptionState>('subscription');

// Basic selectors
export const selectSubscription = createSelector(selectSubscriptionState, (state: SubscriptionState) => state.subscription);

export const selectSubscriptionLoading = createSelector(selectSubscriptionState, (state: SubscriptionState) => state.loading);

export const selectSubscriptionError = createSelector(selectSubscriptionState, (state: SubscriptionState) => state.error);

export const selectSubscriptionLoaded = createSelector(selectSubscriptionState, (state: SubscriptionState) => state.loaded);

export const selectSubscriptionAutoLoadBlocked = createSelector(
  selectSubscriptionState,
  (state: SubscriptionState) => state.autoLoadBlocked
);

// Derived selectors
export const selectHasValidSubscription = createSelector(selectSubscription, (subscription) => {
  if (!subscription || !subscription.status) {
    return false;
  }

  const status = subscription.status.toLowerCase();

  // Check if subscription period has ended
  if (subscription.currentPeriodEnd) {
    const periodEndDate = new Date(subscription.currentPeriodEnd);
    const now = new Date();
    if (periodEndDate < now) {
      // Subscription period has ended, even if status is 'active'
      return false;
    }
  }

  // For trialing subscriptions, also check trialEnd
  if (status === 'trialing' && subscription.trialEnd) {
    const trialEndDate = new Date(subscription.trialEnd);
    const now = new Date();
    if (trialEndDate < now) {
      return false; // Trial has expired
    }
  }

  // Check status - only these statuses are valid (if period hasn't ended)
  return status === 'trialing' || status === 'active' || status === 'past_due';
});

export const selectSubscriptionStatus = createSelector(selectSubscription, (subscription) => subscription?.status || null);

export const selectTrialDaysRemaining = createSelector(selectSubscription, (subscription) => {
  if (subscription?.status === 'trialing') {
    // Use trialEnd if available, otherwise fall back to currentPeriodEnd
    const endDateStr = subscription.trialEnd || subscription.currentPeriodEnd;
    if (endDateStr) {
      const endDate = new Date(endDateStr);
      const now = new Date();
      const diffTime = endDate.getTime() - now.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays > 0 ? diffDays : 0;
    }
  }
  return null;
});

export const selectCustomerId = createSelector(selectSubscription, (subscription) => subscription?.customerId || null);
